import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';

/**
 * Catches render-time errors anywhere below it.
 *
 * React unmounts the whole tree when a render throws, so without a boundary a
 * single bad field on one booking blanks the entire app — white screen, no
 * navbar, no way back. This keeps the failure contained and gives the user a
 * recover path.
 *
 * Must be a class: there is no hook equivalent of componentDidCatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the stack in the console for local debugging. Wire this to a real
    // error reporter (Sentry et al.) when one is added — this is the single
    // place every render error passes through.
    console.error('Unhandled render error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', py: { xs: 8, md: 14 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Something went wrong
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This page hit an unexpected error. Your bookings and payments are
            unaffected.
          </Typography>

          {/* The message is shown in development only — a production stack can
              leak API shapes and internal field names to whoever is looking. */}
          {process.env.NODE_ENV === 'development' && (
            <Box
              component="pre"
              sx={{
                textAlign: 'left',
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflowX: 'auto',
                fontSize: 12,
                mb: 3,
              }}
            >
              {error.toString()}
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" onClick={this.handleReset}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => window.location.assign('/')}>
              Go to home
            </Button>
          </Stack>
        </Box>
      </Container>
    );
  }
}

export default ErrorBoundary;
